/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';
import type { FormatOption } from './config-panel-options';
import type { ManagedBlockGoal, ManagedBlockOutputFormat } from './configuration';

const fieldsetStyles = css({
	borderWidth: 0,
	marginTop: 0,
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
});

const legendStyles = css({
	color: token('color.text.subtle'),
	marginBottom: token('space.100'),
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
});

const formatGridStyles = css({
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	gap: token('space.100'),
});

const formatButtonStyles = css({
	appearance: 'none',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	minWidth: 0,
	minHeight: '72px',
	boxSizing: 'border-box',
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	backgroundColor: token('elevation.surface'),
	color: token('color.text'),
	cursor: 'pointer',
	font: 'inherit',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
	transition: token('motion.button.hovered'),
});

const formatButtonSelectedStyles = css({
	backgroundColor: token('color.background.selected'),
	borderColor: token('color.border.selected'),
});

const formatIconStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: token('space.300'),
	height: token('space.300'),
	marginBottom: token('space.050'),
	color: token('color.icon'),
});

type OutputFormatPickerProps = {
	goal: ManagedBlockGoal;
	isDisabled?: boolean;
	onOutputFormatChange: (outputFormat: ManagedBlockOutputFormat) => void;
	outputFormat: ManagedBlockOutputFormat;
	outputFormatOptions: FormatOption[];
	systemDesignOutputFormatOptions: FormatOption[];
};

export const OutputFormatPicker = ({
	goal,
	isDisabled = false,
	onOutputFormatChange,
	outputFormat,
	outputFormatOptions,
	systemDesignOutputFormatOptions,
}: OutputFormatPickerProps): JSX.Element => {
	const intl = useIntl();
	const visibleOutputFormatOptions =
		goal === 'system-design' ? systemDesignOutputFormatOptions : outputFormatOptions;

	return (
		<fieldset css={fieldsetStyles}>
			<legend css={legendStyles}>{intl.formatMessage(messages.outputFormatLabel)}</legend>
			<div css={formatGridStyles}>
				{visibleOutputFormatOptions.map((option) => {
					const isSelected = outputFormat === option.value;
					const isLocked = Boolean(option.isLocked);

					return (
						<button
							key={option.value}
							aria-disabled={isLocked || undefined}
							aria-pressed={isSelected}
							css={[formatButtonStyles, isSelected && formatButtonSelectedStyles]}
							disabled={isDisabled}
							onClick={() => {
								if (!isLocked) {
									onOutputFormatChange(option.value);
								}
							}}
							type="button"
						>
							<span css={formatIconStyles} aria-hidden="true">
								{option.icon}
							</span>
							<span>{option.label}</span>
						</button>
					);
				})}
			</div>
		</fieldset>
	);
};
